/* eslint-disable react/prop-types -- This component forwards react-hook-form's dynamic form state. */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Checkbox, FormControlLabel, Grid, Icon, TextField, Tooltip, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { brandColors } from 'types/colors';

import DropdownMenu from '../../components/DropdownMenu';
import { ReactComponent as ControllerBoxIcon } from '../../assets/images/icons/controller-box.svg';

const checkboxSx = {
  '& .MuiSvgIcon-root': {
    border: `2px solid ${brandColors.GREY_DARK}`,
  },
  '&.Mui-checked .MuiSvgIcon-root, &.MuiCheckbox-indeterminate .MuiSvgIcon-root': {
    borderColor: brandColors.BLUE,
  },
};

const DeviceConfigurationSections = ({
  canManageUnitConfiguration,
  controlEdit,
  currentUser,
  errorsEdit,
  isControllerType,
  isEmptyValue,
  sectionCardSx,
  sectionTitleSx,
  SENSOR_MODEL_OPTIONS,
  CONTROLLER_MODEL_OPTIONS,
  UNIT_TYPE_OPTIONS,
  UNIT_AIRFLOW_OPTIONS,
  COOLING_TYPE_OPTIONS,
  HEATING_TYPE_OPTIONS,
  NOX_AVAILABLE_OPTIONS,
  RADAR_AVAILABLE_OPTIONS,
  INACTIVE_DEVICE_NOTIFICATION_OPTIONS,
  watchEditDeviceIsController,
  watchEditDeviceIsSensor,
  watchEditUnitType,
}) => (
  <>
    <Grid item xs={12}>
      <Typography sx={sectionTitleSx}>
        <InfoOutlinedIcon sx={{ color: '#6a5ae0' }} />
        General Info
      </Typography>
      <Box sx={sectionCardSx}>
        <Grid container spacing={2}>
          {/* Device Name */}
          <Grid item xs={12} md={6}>
            <Controller
              name="device_name"
              control={controlEdit}
              rules={{ required: 'Device name is required' }}
              render={({ field: { onChange, value } }) => (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Device Name
                  </Typography>
                  <TextField
                    value={value || ''}
                    placeholder="Enter device name"
                    onChange={onChange}
                    fullWidth
                    variant="outlined"
                    error={!!errorsEdit.device_name}
                    helperText={errorsEdit.device_name?.message}
                    InputProps={{
                      sx: {
                        backgroundColor: '#ffffff',
                        borderRadius: 2,
                        '& .MuiInputBase-input': {
                          py: 1.4,
                          px: 1.5,
                        },
                        '& fieldset': { border: 'none' },
                        '&:hover fieldset': { border: 'none' },
                        '&.Mui-focused fieldset': { border: 'none' },
                      },
                    }}
                  />
                </Box>
              )}
            />
          </Grid>
          {canManageUnitConfiguration && (
            <>
              <Grid item xs={12} md={6} sx={{ mt: { sm: 5 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { sm: 'center' },
                    justifyContent: 'space-evenly',
                  }}>
                  <Box>
                    <Controller
                      name="device_is_sensor"
                      control={controlEdit}
                      render={({ field: { onChange, value } }) => (
                        <FormControlLabel
                          sx={{
                            mt: 0,
                            color: value ? brandColors.BLUE : brandColors.GREY,
                            '&.Mui-disabled': { opacity: 1 },
                            '& .MuiFormControlLabel-label.Mui-disabled': {
                              color: value ? brandColors.BLUE : brandColors.GREY,
                            },
                          }}
                          control={
                            <Checkbox
                              checked={!!value}
                              disabled={!!value && !watchEditDeviceIsController}
                              onChange={event => {
                                if (event.target.checked || watchEditDeviceIsController) {
                                  onChange(event.target.checked);
                                }
                              }}
                              sx={checkboxSx}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Icon fontSize="large" color="inherit">
                                sensors
                              </Icon>
                              Sensor
                            </Box>
                          }
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <Controller
                      name="device_is_controller"
                      control={controlEdit}
                      render={({ field: { onChange, value } }) => (
                        <FormControlLabel
                          sx={{
                            mt: 0,
                            color: value ? brandColors.BLUE : brandColors.GREY,
                            '&.Mui-disabled': { opacity: 1 },
                            '& .MuiFormControlLabel-label.Mui-disabled': {
                              color: value ? brandColors.BLUE : brandColors.GREY,
                            },
                          }}
                          control={
                            <Checkbox
                              checked={!!value}
                              disabled={!!value && !watchEditDeviceIsSensor}
                              onChange={event => {
                                if (event.target.checked || watchEditDeviceIsSensor) {
                                  onChange(event.target.checked);
                                }
                              }}
                              sx={checkboxSx}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box
                                component={ControllerBoxIcon}
                                sx={{ width: '1.5rem', height: '1.5rem', '& path': { fill: 'currentColor' } }}
                              />
                              Controller
                            </Box>
                          }
                        />
                      )}
                    />
                  </Box>
                  <Tooltip
                    arrow
                    title="Devices configured solely as controllers are excluded from sensor-data pages and sensor-driven components, including the Building Demand features">
                    <Box component="span" sx={{ display: 'inline-flex', cursor: 'help' }}>
                      <InfoOutlinedIcon sx={{ color: brandColors.GREY_DARK }} />
                    </Box>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="sensor_model"
                  control={controlEdit}
                  render={({ field: { onChange, value } }) => (
                    <DropdownMenu
                      options={SENSOR_MODEL_OPTIONS}
                      selectedOption={value || ''}
                      handleChange={onChange}
                      labelName="Sensor model"
                      error={!!errorsEdit.sensor_model}
                      inline={true}
                      labelWidth="15rem"
                    />
                  )}
                />
              </Grid>
              {/* Controller model type Dropdown ( Controller only ) */}
              {isControllerType && (
                <Grid item xs={12} md={6}>
                  <Controller
                    name="controller_model"
                    control={controlEdit}
                    render={({ field: { onChange, value } }) => {
                      const isDisabled = !isControllerType;

                      return (
                        <DropdownMenu
                          disabled={isDisabled}
                          options={CONTROLLER_MODEL_OPTIONS}
                          selectedOption={isEmptyValue(value) ? '' : String(value)}
                          handleChange={onChange}
                          labelName="Controller model"
                          error={!!errorsEdit.controller_model}
                          inline={true}
                          sx={{
                            fontSize: '0.5rem', // or '1rem', '0.875rem', etc.
                            '& .MuiSelect-select': { fontSize: '0.5rem' },
                            '& .MuiInputLabel-root': {
                              fontSize: '0.5rem',
                            },
                          }}
                          labelWidth="15rem"
                        />
                      );
                    }}
                  />
                </Grid>
              )}
              {/* Unit Type */}
              {isControllerType && watchEditUnitType === 'AHU' && (
                <Grid item xs={12} md={6}>
                  <Controller
                    name="unit_type"
                    control={controlEdit}
                    render={({ field: { onChange, value } }) => (
                      <DropdownMenu
                        options={UNIT_TYPE_OPTIONS}
                        selectedOption={value || ''}
                        handleChange={onChange}
                        labelName="Unit type"
                        error={!!errorsEdit.unit_type}
                        inline={true}
                        sx={{
                          fontSize: '0.5rem',
                          '& .MuiSelect-select': { fontSize: '0.5rem' },
                          '& .MuiInputLabel-root': { fontSize: '0.5rem' },
                        }}
                        labelWidth="15rem"
                      />
                    )}
                  />
                </Grid>
              )}
              {isControllerType && watchEditUnitType !== 'AHU' && (
                <Grid item xs={12} md={6}>
                  <Controller
                    name="unit_type"
                    control={controlEdit}
                    render={({ field: { onChange, value } }) => (
                      <DropdownMenu
                        options={UNIT_TYPE_OPTIONS}
                        selectedOption={value || ''}
                        handleChange={onChange}
                        labelName="Unit type"
                        error={!!errorsEdit.unit_type}
                        inline={true}
                        sx={{
                          fontSize: '0.5rem',
                          '& .MuiSelect-select': { fontSize: '0.5rem' },
                          '& .MuiInputLabel-root': { fontSize: '0.5rem' },
                        }}
                        labelWidth="15rem"
                      />
                    )}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Box>
    </Grid>

    {canManageUnitConfiguration && (
      <Grid item xs={12}>
        <Typography sx={sectionTitleSx}>
          <TuneIcon sx={{ color: '#6a5ae0' }} />
          Unit Configuration
        </Typography>
        <Box sx={sectionCardSx}>
          <Grid container spacing={2}>
            {isControllerType && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="area_option"
                  control={controlEdit}
                  render={({ field: { onChange, value } }) => (
                    <DropdownMenu
                      options={watchEditUnitType === 'AHU' ? UNIT_AIRFLOW_OPTIONS.AHU : UNIT_AIRFLOW_OPTIONS.FCU}
                      selectedOption={value || ''}
                      handleChange={onChange}
                      labelName={watchEditUnitType === 'AHU' ? 'Area and Fan kW' : 'Area and CFM'}
                      error={!!errorsEdit.area_option}
                      inline={true}
                      labelWidth="15rem"
                    />
                  )}
                />
              </Grid>
            )}
            {isControllerType && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="cooling_type"
                  control={controlEdit}
                  render={({ field: { onChange, value } }) => (
                    <DropdownMenu
                      options={COOLING_TYPE_OPTIONS}
                      selectedOption={value || ''}
                      handleChange={onChange}
                      labelName="Cooling"
                      error={!!errorsEdit.cooling_type}
                      inline={true}
                      labelWidth="15rem"
                    />
                  )}
                />
              </Grid>
            )}
            {isControllerType && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="heating_type"
                  control={controlEdit}
                  render={({ field: { onChange, value } }) => (
                    <DropdownMenu
                      options={watchEditUnitType === 'AHU' ? HEATING_TYPE_OPTIONS.AHU : HEATING_TYPE_OPTIONS.FCU}
                      selectedOption={value || ''}
                      handleChange={onChange}
                      labelName="Heating"
                      error={!!errorsEdit.heating_type}
                      inline={true}
                      labelWidth="15rem"
                    />
                  )}
                />
              </Grid>
            )}
            {isControllerType && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="slave_id"
                  control={controlEdit}
                  rules={{
                    min: { value: 0, message: 'Slave ID must be between 0 and 24' },
                    max: {
                      value: 24,
                      message: 'Slave ID must be between 0 and 24',
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                      }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Slave ID
                      </Typography>
                      <TextField
                        value={isEmptyValue(value) ? '' : value}
                        placeholder="Enter slave ID (0-24)"
                        type="number"
                        onChange={event => {
                          const rawValue = event.target.value;
                          if (rawValue === '') {
                            onChange('');
                            return;
                          }
                          const parsed = Number(rawValue);
                          if (!Number.isFinite(parsed)) {
                            return;
                          }
                          const bounded = Math.min(24, Math.max(0, parsed));
                          onChange(bounded);
                        }}
                        fullWidth
                        variant="outlined"
                        error={!!errorsEdit.slave_id}
                        helperText={errorsEdit.slave_id?.message}
                        inputProps={{ min: 0, max: 24, step: 1 }}
                        InputProps={{
                          sx: {
                            backgroundColor: '#ffffff',
                            borderRadius: 2,
                            '& .MuiInputBase-input': {
                              py: 1.4,
                              px: 1.5,
                            },
                            '& fieldset': { border: 'none' },
                            '&:hover fieldset': { border: 'none' },
                            '&.Mui-focused fieldset': { border: 'none' },
                          },
                        }}
                      />
                    </Box>
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Controller
                name="has_nox"
                control={controlEdit}
                render={({ field: { onChange, value } }) => (
                  <DropdownMenu
                    options={NOX_AVAILABLE_OPTIONS}
                    selectedOption={isEmptyValue(value) ? '' : String(value)}
                    handleChange={onChange}
                    labelName="Nox available"
                    error={!!errorsEdit.has_nox}
                    inline={true}
                    labelWidth="15rem"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="is_using_radar"
                control={controlEdit}
                render={({ field: { onChange, value } }) => (
                  <DropdownMenu
                    options={RADAR_AVAILABLE_OPTIONS}
                    selectedOption={isEmptyValue(value) ? 'false' : String(value)}
                    handleChange={onChange}
                    labelName="Radar available"
                    error={!!errorsEdit.is_using_radar}
                    inline={true}
                    labelWidth="15rem"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="inactive_device_notification_enabled"
                control={controlEdit}
                render={({ field: { onChange, value } }) => (
                  <DropdownMenu
                    options={INACTIVE_DEVICE_NOTIFICATION_OPTIONS}
                    selectedOption={isEmptyValue(value) ? 'false' : String(value)}
                    handleChange={onChange}
                    labelName="Inactive notification"
                    error={!!errorsEdit.inactive_device_notification_enabled}
                    inline={true}
                    labelWidth="15rem"
                  />
                )}
              />
            </Grid>
            {currentUser?.role === 's' && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="co2_delta_for_ocp"
                  control={controlEdit}
                  render={({ field: { onChange, value } }) => (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                      }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        CO₂ Delta
                      </Typography>
                      <TextField
                        value={isEmptyValue(value) ? '' : value}
                        placeholder="Enter CO₂ delta"
                        type="number"
                        onChange={event => {
                          const rawValue = event.target.value;
                          if (rawValue === '') {
                            onChange('');
                            return;
                          }
                          const parsed = Number(rawValue);
                          if (!Number.isFinite(parsed)) {
                            return;
                          }
                          onChange(parsed);
                        }}
                        fullWidth
                        variant="outlined"
                        error={!!errorsEdit.co2_delta_for_ocp}
                        helperText={errorsEdit.co2_delta_for_ocp?.message}
                        inputProps={{ step: 1 }}
                        InputProps={{
                          sx: {
                            backgroundColor: '#ffffff',
                            borderRadius: 2,
                            '& .MuiInputBase-input': {
                              py: 1.4,
                              px: 1.5,
                            },
                            '& fieldset': { border: 'none' },
                            '&:hover fieldset': { border: 'none' },
                            '&.Mui-focused fieldset': { border: 'none' },
                          },
                        }}
                      />
                    </Box>
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </Grid>
    )}
  </>
);

export default DeviceConfigurationSections;
